import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ChangeDetectorRef, Output, EventEmitter, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { LocaleService } from 'src/app/services/locale.service';
import { QaiTableComponent, TableRow, QaiLoadingComponent, HeaderConfig, SortDirection } from '@qai/ui';
import { ICertificationAssignment } from 'src/app/interfaces/reportingV2/icertification-assignments';
import { ICertificationAssignmentStatsResponse } from 'src/app/interfaces/reportingV2/icertification-assignment-stats-response';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';

@Component({
  selector: 'app-certification-table',
  templateUrl: './certification-table.component.html',
  styleUrls: ['./certification-table.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    },
  ],
  imports: [
    TranslocoModule,
    CommonModule,
    QaiTableComponent,
    QaiLoadingComponent,
  ],
})
export class CertificationTableComponent implements OnInit, OnChanges {
  @Input() certificationStats: ICertificationAssignmentStatsResponse[] = [];
  @Output() visibleStatsChanged = new EventEmitter<ICertificationAssignmentStatsResponse[]>();

  @ViewChild(QaiTableComponent) tableComponent?: QaiTableComponent;

  isTranslationLoaded: boolean = false;
  isLoading: boolean = false;
  tableData: TableRow[] = [];
  headers: HeaderConfig[] = [];
  initialSortColumn: string = '';
  initialSortDirection: SortDirection = 'desc';
  currentPage: number = 1;
  certificationStatus: string = '';
  showAssignmentDetail: boolean = false;
  private idToStatsMap = new Map<string | number, ICertificationAssignmentStatsResponse>();

  certificationAssignments: ICertificationAssignment[] = [];

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private localeService: LocaleService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private featureFlagsService: FeatureFlagsService,
    private amplitudeService: AmplitudeService
  ) { }

  async ngOnInit() {
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      this.initializeTable();
    });
    this.showAssignmentDetail = await this.featureFlagsService.isFlagEnabled(
      AppConstants.INDIVIDUAL_ASSIGNMENT_VIEW
    );
    console.log(
      `Flag for INDIVIDUAL_ASSIGNMENT_VIEW: ${this.showAssignmentDetail}`
    );
  }

  onPageChanged(event: { page: number; pageSize: number }): void {
    this.currentPage = event.page;
    this.emitVisibleStats();
  }

  onSortChanged(event: { column: string; direction: 'asc' | 'desc' | null }): void {
    this.emitVisibleStats();
  }

  emitVisibleStats(): void {
    if (!this.tableComponent || !this.certificationStats || this.certificationStats.length === 0) {
      this.visibleStatsChanged.emit([]);
      return;
    }

    setTimeout(() => {
      const paginatedData = this.tableComponent!.paginatedData;
      
      const visibleStats = paginatedData
        .map(row => this.idToStatsMap.get(row.id))
        .filter((stat): stat is ICertificationAssignmentStatsResponse => stat !== undefined);
      
      this.visibleStatsChanged.emit(visibleStats);
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['certificationStats']) {
      setTimeout(() => {
        this.updateTableData();
      }, 0);
    }
  }

  private updateTableData(): void {
    if (this.certificationStats && this.certificationStats.length > 0) {
      this.idToStatsMap.clear();

      const convertedAssignments: ICertificationAssignment[] = this.certificationStats.map(stat => {
        this.idToStatsMap.set(stat.moduleAssignmentUuid, stat);

        const totalUsers = stat.assignedUsersCount + stat.inProgressUsersCount +
          stat.outOfAttemptsUsersCount + stat.certifiedUsersCount;

        const inProgressPercentage = totalUsers > 0
          ? Math.round((stat.inProgressUsersCount / totalUsers) * 100)
          : 0;

        const outOfAttemptsPercentage = totalUsers > 0
          ? Math.round((stat.outOfAttemptsUsersCount / totalUsers) * 100)
          : 0;

        const certifiedPercentage = totalUsers > 0
          ? Math.round((stat.certifiedUsersCount / totalUsers) * 100)
          : 0;

        return {
          id: stat.moduleAssignmentUuid,
          name: stat.moduleAssignmentName,
          startDate: stat.startDate,
          endDate: stat.endDate,
          usersAssigned: totalUsers,
          inProgress: inProgressPercentage,
          outOfAttempts: outOfAttemptsPercentage,
          certified: certifiedPercentage,
        };
      });

      this.currentPage = 1;
      this.tableData = this.createTableRows(convertedAssignments);
      this.setInitialSortColumn();
      this.cdr.detectChanges();
      this.emitVisibleStats();
    } else {
      this.currentPage = 1;
      this.tableData = [];
      this.setInitialSortColumn();
      this.idToStatsMap.clear();
      this.visibleStatsChanged.emit([]);
      console.log('No certification stats, clearing table data');
      this.cdr.detectChanges();
    }
  }

  private initializeTable() {
    this.headers = [
      {
        name: this.translocoService.translate(
          'ASSIGNMENT_NAME',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '20%',
      },
      {
        name: this.translocoService.translate(
          'START_DATE_LABEL',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '12%',
      },
      {
        name: this.translocoService.translate(
          'END_DATE_LABEL',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '12%',
      },
      {
        name: this.translocoService.translate(
          'USERS_ASSIGNED_LABEL',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '12%',
        clickable: true,
      },
      {
        name: this.translocoService.translate(
          'IN_PROGRESS_LABEL',
          {},
          'reportingV2'
        ),
        icon: 'info-circle',
        tooltip: this.translocoService.translate(
          'IN_PROGRESS_TOOLTIP',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '14%',
        clickable: true,
      },
      {
        name: this.translocoService.translate(
          'OUT_OF_ATTEMPTS_LABEL',
          {},
          'reportingV2'
        ),
        icon: 'info-circle',
        tooltip: this.translocoService.translate(
          'OUT_OF_ATTEMPTS_TOOLTIP',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '15%',
        clickable: true,
      },
      {
        name: this.translocoService.translate(
          'CERTIFIED_LABEL',
          {},
          'reportingV2'
        ),
        icon: 'info-circle',
        tooltip: this.translocoService.translate(
          'CERTIFIED_TOOLTIP',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '15%',
        clickable: true,
      },
    ];

    this.initialSortDirection = 'desc';

    this.tableData = this.createTableRows(this.certificationAssignments);
    this.setInitialSortColumn();
  }

  private setInitialSortColumn(): void {
    // Check if all End date properties in tableData are empty strings
    const endDateColumnName = this.translocoService.translate('END_DATE_LABEL', {}, 'reportingV2');
    const allEndDatesEmpty = this.tableData.every(row => {
      const endDateCell = row[endDateColumnName];
      return endDateCell && typeof endDateCell === 'object' && 'value' in endDateCell 
        ? endDateCell.value === '' || endDateCell.value === null || endDateCell.value === undefined || 
          (typeof endDateCell.value === 'string' && endDateCell.value.trim() === '')
        : true;
    });

    if (allEndDatesEmpty && this.tableData.length > 0) {
      // If all End dates are empty, use START_DATE_LABEL as initial sort column
      this.initialSortColumn = this.translocoService.translate(
        'START_DATE_LABEL',
        {},
        'reportingV2'
      );
    } else {
      // Default to END_DATE_LABEL
      this.initialSortColumn = this.translocoService.translate(
        'END_DATE_LABEL',
        {},
        'reportingV2'
      );
    }
    
    this.initialSortDirection = 'desc';
  }

  private createTableRows(assignments: ICertificationAssignment[]): TableRow[] {
    return assignments.map((assignment) => {
      const row: any = {
        id: assignment.id,
        [this.translocoService.translate('ASSIGNMENT_NAME', {}, 'reportingV2')]:
          { type: 'text', value: assignment.name },
        [this.translocoService.translate(
          'START_DATE_LABEL',
          {},
          'reportingV2'
        )]: { type: 'text', value: this.localeService.formatTableDate(assignment.startDate) },
        [this.translocoService.translate('END_DATE_LABEL', {}, 'reportingV2')]:
          { type: 'text', value: this.localeService.formatTableDate(assignment.endDate) },
        [this.translocoService.translate(
          'USERS_ASSIGNED_LABEL',
          {},
          'reportingV2'
        )]: { type: 'text', value: assignment.usersAssigned.toString() },
        [this.translocoService.translate(
          'IN_PROGRESS_LABEL',
          {},
          'reportingV2'
        )]: {
          type: 'text',
          value: this.localeService.formatPercentage(
            assignment.inProgress,
            this.localeService.getDecimalPlaces(assignment.inProgress)
          ),
        },
        [this.translocoService.translate(
          'OUT_OF_ATTEMPTS_LABEL',
          {},
          'reportingV2'
        )]: {
          type: 'text',
          value: this.localeService.formatPercentage(
            assignment.outOfAttempts,
            this.localeService.getDecimalPlaces(assignment.outOfAttempts)
          ),
        },
        [this.translocoService.translate('CERTIFIED_LABEL', {}, 'reportingV2')]: {
          type: 'text',
          value: this.localeService.formatPercentage(
            assignment.certified,
            this.localeService.getDecimalPlaces(assignment.certified)
          ),
        },
      };

      return row as TableRow;
    });
  }

  onTableRowClick(event: { rowId: string | number; rowData: TableRow }) {
    console.log('Row clicked:', event);

    // Track certification assignment click
    const assignmentName = this.extractAssignmentName(event.rowData);
    console.log("asssignment nameeEEeeeeeeeee", assignmentName)
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_CERTIFICATIONASSIGNMENT, {
      assignment_name: assignmentName
    });

    if (this.showAssignmentDetail) {
      this.navigateToCertificationDetail(event.rowId);
    }
  }

  navigateToCertificationDetail(certificationId: string | number) {
    const assignmentData = this.idToStatsMap.get(certificationId);
    
    if (assignmentData) {
      this.router.navigate(
        ['/reporting_dashboard/certification', certificationId],
        {
          queryParams: { certification_status: this.certificationStatus },
          state: { 
            heading: assignmentData.moduleAssignmentName,
            startDate: assignmentData.startDate,
            endDate: assignmentData.endDate,
            callType: AppConstants.TYPE_CERTIFICATION,
            scoreCriteria: assignmentData.scoreCriteria
          }
        }
      );
    }
  }


  private extractAssignmentName(rowData: TableRow): string {
    const assignmentNameKey = this.translocoService.translate('ASSIGNMENT_NAME', {}, 'reportingV2');
    const cell = rowData[assignmentNameKey];

    if (cell && typeof cell === 'object' && 'value' in cell) {
      const value = cell.value;
      if (typeof value === 'string') {
        return value || 'Unknown';
      } else if (Array.isArray(value)) {
        return value.join(', ') || 'Unknown';
      }
    }

    return 'Unknown';
  }

  onCellClick($event: { rowData: TableRow; columnName: string; event: Event }) {
    this.certificationStatus = $event.columnName;

    let status = $event.columnName;

    // Track user count cell click
    const assignmentName = this.extractAssignmentName($event.rowData);
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_USERCOUNTCELL, {
      assignment_name: assignmentName,
      status: status
    });

    if (this.showAssignmentDetail) {
      this.navigateToCertificationDetail($event.rowData.id);
    }
  }
}
